import { isUndefined } from 'lodash';
import { Graphics, Sprite, Text, TextStyle, Texture, Renderer, Assets, Container } from 'pixi.js';

import CTAItem from 'entity/CTAItem';
import ButtonSize from 'enums/ButtonSize';

import { COLOR_WHITE, FONT_FAMILY_MEDIUM, COLOR_BLACK, COLOR_GREY } from '../../constants';
import { ZIndex } from '../constants';

export const BUTTON_HEIGHT = 40;
export const BUTTON_WIDTH = 200;

export default class CTARoomItem extends Container {
  private mouseover?: () => void;
  private mouseout?: () => void;
  private pointerdown?: () => void;

  public buttonSprite?: Sprite;
  private buttonText?: Text;

  private imageTexture: Texture = Texture.EMPTY;
  public imageSprite?: Sprite;

  public itemID?: number;
  private renderer: Renderer;

  public constructor(
    renderer: Renderer,
    {
      compressed,
      item,
      mouseout,
      mouseover,
      pointerdown,
      ratio,
    }: {
      item: CTAItem;
      ratio: number;
      pointerdown: () => void;
      mouseover?: () => void;
      mouseout?: () => void;
      compressed: boolean;
    },
  ) {
    super();

    this.x = item.x ?? 0;
    this.y = item.y ?? 0;
    this.zIndex = item.traversable ? ZIndex.Artifact : ZIndex.CTA;
    this.name = `cta-container-${item.name}`;
    this.mouseout = mouseout;
    this.mouseover = mouseover;
    this.pointerdown = pointerdown;
    this.renderer = renderer;

    if (item.imageURI) {
      void this.load(item, ratio, compressed);
    }

    if (item.buttonVisible) {
      this.createButton(item, ratio);
    }
  }

  private createButton(item: CTAItem, ratio: number): void {
    const buttonY = item.imageURI ? generateButtonY(this.y, item.imageHeight ?? 0) : this.y;
    this.itemID = item.CTAItemId;
    const hasImage = !!item.imageURI;

    const buttonContainer = new Container();
    buttonContainer.name = `cta-button-container-${item.CTAItemId}`;
    buttonContainer.eventMode = 'static';
    buttonContainer.cursor = 'pointer';

    const text = item.text ?? '';
    this.buttonText = createButtonText(text, ratio, item.buttonTextColor);
    this.buttonText.anchor.set(0.5, 0.5);

    const buttonTexture = generateButtonTexture(this.renderer, ratio, item.buttonColor);

    this.buttonSprite = Sprite.from(buttonTexture);
    this.buttonSprite.name = `button-${item.CTAItemId}`;
    this.buttonSprite.height = BUTTON_HEIGHT * ratio;
    this.buttonSprite.width = BUTTON_WIDTH * ratio;
    this.buttonSprite.anchor.set(0.5, 0.5);
    this.buttonSprite.visible = true;
    if (hasImage) {
      this.buttonText.y = buttonY;
      this.buttonSprite.y = buttonY;
    }

    buttonContainer
      .on('pointerdown', () => this.pointerdown?.())
      .on('pointerenter', () => {
        this.mouseover?.();
        if (this.buttonText) {
          this.buttonText.style.fill = item.buttonTextColor ?? COLOR_GREY;
        }
      })
      .on('pointerleave', () => {
        this.mouseout?.();
        if (this.buttonText) {
          this.buttonText.style.fill = item.buttonTextColor ?? COLOR_BLACK;
        }
      });

    this.addChild(buttonContainer).addChild(this.buttonSprite, this.buttonText);
  }

  public load(item: CTAItem, ratio: number, compressed: boolean): Promise<void> {
    const loadPromise = new Promise<void>(async (resolve, reject) => {
      const { imageHeight, imageURI, imageWidth } = item;

      if (!imageURI) {
        reject('Invalid imageURI');
        return;
      }

      if (isUndefined(imageWidth) || isUndefined(imageHeight)) {
        reject('Invalid image dimensions');
        return;
      }

      // TODO: this does not work, image is being loaded twice
      if (this.imageSprite) {
        return;
      }
      const height = imageHeight * ratio;
      const width = imageWidth * ratio;

      this.imageTexture = await Assets.load(
        //(compressed ? item.imageURI?.replace('webp', 'ktx2') :
        item.imageURI ?? '',
        //) ?? '',
      );
      this.imageSprite = Sprite.from(this.imageTexture);
      this.imageSprite.name = `cta-image-${item.CTAItemId}`;
      this.imageSprite.height = height;
      this.imageSprite.width = width;

      this.imageSprite.anchor.set(0.5, 0.5);
      this.imageSprite.visible = true;
      this.addChild(this.imageSprite);
      return resolve();
    });

    return loadPromise;
  }

  public restoreSprites(): { buttonSprite?: Sprite; imageSprite?: Sprite } {
    return { buttonSprite: this.buttonSprite, imageSprite: this.imageSprite };
  }

  protected buttonSizeExample = new Map<ButtonSize, ButtonSizeInfo>([
    [
      ButtonSize.SMALL,
      {
        buttonSize: ButtonSize.SMALL,
        width: 137,
        height: 42,
        fontSize: 12,
      },
    ],
    [
      ButtonSize.MEDIUM,
      {
        buttonSize: ButtonSize.MEDIUM,
        width: 188,
        height: 49,
        fontSize: 16,
      },
    ],
    [
      ButtonSize.LARGE,
      {
        buttonSize: ButtonSize.LARGE,
        width: 280,
        height: 71,
        fontSize: 30,
      },
    ],
  ]);
}

// Create button
export const createButtonText = (text: string, ratio: number, textColor?: string): Text => {
  const size = 20 * ratio;
  const style = new TextStyle({
    fill: textColor ?? COLOR_BLACK,
    fontFamily: FONT_FAMILY_MEDIUM,
    fontSize: `${size}px`,
    align: 'center',
    padding: 2,
  });

  const formattedText = text;

  const buttonText = new Text(formattedText, style);
  const textWidth = Math.floor(buttonText.width);
  const maxLength = Math.floor(textWidth / size);

  if (textWidth / ratio > BUTTON_WIDTH) {
    let index = maxLength - 1;
    if (maxLength > 16) {
      index = 14;
    }
    const t = text.slice(0, index);
    buttonText.text = t.concat('...');
  }

  buttonText.anchor.set(0.5, 0.5);
  return buttonText;
};

export const generateButtonTexture = (renderer: Renderer, ratio: number, buttonColor?: string): Texture => {
  const buttonRadius = 25;
  const height = BUTTON_HEIGHT * ratio;
  const width = BUTTON_WIDTH * ratio;
  const graphics = new Graphics()
    .beginFill(buttonColor ?? COLOR_WHITE)
    .drawRoundedRect(0, 0, width, height, buttonRadius)
    .endFill()
    .lineStyle(1, buttonColor ?? COLOR_WHITE);
  return renderer.generateTexture(graphics);
};

export const generateButtonY = (y: number, imageHeight: number): number => {
  return imageHeight / 2 + BUTTON_HEIGHT / 2 + 20;
};

export const generateMapButtonY = (y: number, imageHeight: number, ratio: number): number => {
  return y + (imageHeight * ratio) / 2 + (BUTTON_HEIGHT * ratio) / 2 + 20 * ratio + 2;
};

interface ButtonSizeInfo {
  buttonSize: ButtonSize;
  width: number;
  height: number;
  fontSize: number;
}
